import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Linking, Platform } from 'react-native';
import { useNetworkState } from 'expo-network';
import { useTranslation } from 'react-i18next';
import { useToast } from './useToast';
import { useVoiceRecorder } from './useVoiceRecorder';
import { useVoiceUsage, type VoiceUsageSnapshot } from './useVoiceUsage';
import { useVoiceHaptics } from '../lib/voice/haptics';
import {
  trackVoiceCapReached,
  trackVoiceOfflineFallback,
  trackVoicePermDenied,
  trackVoiceSessionStarted,
  trackVoiceTranscribeFailed,
  trackVoiceTranscribeSuccess,
  type VoiceRole,
} from '../lib/voice/telemetry';
import { transcribeVoiceAudio, type VoiceLanguage } from '../lib/voice/api';
import {
  getMicrophonePermissionStatus,
  type MicrophonePermissionStatus,
} from '../lib/voice/audio';

/**
 * `listening` — the mic is open. `transcribing` — the user has stopped and the
 * recording is on its way to the transcription model.
 */
export type VoiceInputStatus = 'idle' | 'listening' | 'transcribing';

export type VoiceInputErrorKind = 'permission' | 'unavailable' | 'network' | 'unknown';

export interface VoiceInputController {
  status: VoiceInputStatus;
  /** 0..1 mic level while listening. Drives Nova's face and the level meter. */
  level: number;
  /** The kind of the most recent failure, kept until the next attempt. */
  lastError?: VoiceInputErrorKind;
  /** Start when idle, stop-and-finish when listening. The mic button's one action. */
  toggle: () => void;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  /** Throw the session away without delivering any text. */
  cancel: () => Promise<void>;
  isOverCap: boolean;
  isOffline: boolean;
  permissionStatus: MicrophonePermissionStatus;
  usage?: VoiceUsageSnapshot;
}

export interface UseVoiceInputArgs {
  role: VoiceRole;
  surface?: 'composer' | 'handsfree';
  /**
   * Language hint for the transcription model. Defaults to `auto`: the UI
   * language says nothing about the language spoken, and the model detects
   * it (including English/Arabic mixed in one sentence).
   */
  language?: VoiceLanguage;
  sessionId?: string;
  /** Called once per session with the finished transcript. Never called with an empty one. */
  onTranscript: (transcript: string) => void;
  onCapReached?: () => void;
  onPermDenied?: () => void;
  /**
   * When this flips to false mid-session (e.g. the surface swaps its controls
   * out), the session is discarded so the mic can't stay open with no stop
   * button on screen. Defaults to true.
   */
  enabled?: boolean;
}

/** A tap-and-release this short is a mis-tap, not speech: don't bill a request for it. */
const MIN_RECORDING_MS = 400;

/**
 * Voice input for every text field: records on the device (expo-audio, which
 * is MediaRecorder in browsers) and transcribes on the server with Gemini 3.5
 * Transcribe via `/api/voice/transcribe`.
 *
 * The design is listen-then-write: nothing is written into the field while the
 * user is talking. The recording is sent once, after the user stops, and the
 * transcript is handed back once so the caller can land it in one reveal.
 *
 * Each session gets an id; a transcript that comes back for a session that was
 * cancelled, or replaced by a newer one, is dropped.
 */
export function useVoiceInput(args: UseVoiceInputArgs): VoiceInputController {
  const { role, surface = 'composer', language = 'auto', sessionId, onPermDenied, enabled = true } = args;
  const { t } = useTranslation('common');
  const toast = useToast();
  const haptics = useVoiceHaptics();
  const recorder = useVoiceRecorder();
  const usageQuery = useVoiceUsage(enabled);
  const networkState = useNetworkState();
  const isOffline = networkState.isConnected === false;

  const [status, setStatus] = useState<VoiceInputStatus>('idle');
  const [lastError, setLastError] = useState<VoiceInputErrorKind | undefined>(undefined);
  const [permissionStatus, setPermissionStatus] =
    useState<MicrophonePermissionStatus>('undetermined');

  const statusRef = useRef<VoiceInputStatus>('idle');
  const sessionIdRef = useRef(0);
  const startedAtRef = useRef(0);
  const startingRef = useRef(false);
  const recorderRef = useRef(recorder);
  recorderRef.current = recorder;
  const onTranscriptRef = useRef(args.onTranscript);
  onTranscriptRef.current = args.onTranscript;
  const onCapReachedRef = useRef(args.onCapReached);
  onCapReachedRef.current = args.onCapReached;

  const setStatusBoth = useCallback((next: VoiceInputStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  // Probe permission on mount and on foreground (the user may have changed it
  // in Settings). Browsers ask on first use.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const next = await getMicrophonePermissionStatus();
        if (!cancelled) setPermissionStatus(next);
      } catch {
        // Probe only; start() asks properly.
      }
    };
    void refresh();
    const sub = AppState.addEventListener('change', (next) => {
      if (next === 'active') void refresh();
    });
    return () => {
      cancelled = true;
      sub.remove();
    };
  }, []);

  const reportError = useCallback(
    (kind: VoiceInputErrorKind) => {
      setLastError(kind);
      if (kind === 'permission') {
        toast.error(t('chat.dictationPermissionDeniedTitle'), t('chat.dictationPermissionSettingsMessage'));
      } else if (kind === 'network') {
        toast.error(t('voice.transcribe.offlineTitle'), t('voice.transcribe.offlineMessage'));
      } else if (kind === 'unavailable') {
        toast.error(t('chat.dictationUnavailableTitle'), t('chat.dictationUnavailableMessage'));
      } else {
        toast.error(t('voice.transcribe.errorTitle'), t('voice.transcribe.errorMessage'));
      }
    },
    [t, toast],
  );

  const reachCap = useCallback(
    (usage?: VoiceUsageSnapshot) => {
      haptics.trigger('error');
      trackVoiceCapReached({ role, surface, usedMs: usage?.usedMs ?? 0, capMs: usage?.capMs ?? 0 });
      onCapReachedRef.current?.();
    },
    [haptics, role, surface],
  );

  const start = useCallback(async () => {
    if (statusRef.current !== 'idle' || startingRef.current) return;
    setLastError(undefined);
    if (isOffline) {
      haptics.trigger('error');
      trackVoiceOfflineFallback({ role, surface });
      reportError('network');
      return;
    }
    if (usageQuery.data?.isOverCap) {
      reachCap(usageQuery.data);
      return;
    }

    startingRef.current = true;
    try {
      const started = await recorderRef.current.start();
      if (!started.ok) {
        haptics.trigger('error');
        if (started.reason === 'permission') {
          setPermissionStatus(started.permissionStatus);
          trackVoicePermDenied({ role, surface });
          onPermDenied?.();
          reportError('permission');
          if (started.permissionStatus === 'denied' && Platform.OS !== 'web') void Linking.openSettings();
        } else {
          reportError('unavailable');
        }
        return;
      }
      setPermissionStatus('granted');
      sessionIdRef.current += 1;
      startedAtRef.current = Date.now();
      haptics.trigger('mic-on');
      trackVoiceSessionStarted({ role, surface });
      setStatusBoth('listening');
    } finally {
      startingRef.current = false;
    }
  }, [haptics, isOffline, onPermDenied, reachCap, reportError, role, setStatusBoth, surface, usageQuery.data]);

  const stop = useCallback(async () => {
    if (statusRef.current !== 'listening') return;
    const session = sessionIdRef.current;
    const heldMs = Date.now() - startedAtRef.current;
    haptics.trigger('mic-off');
    setStatusBoth('transcribing');

    const stopped = await recorderRef.current.stop();
    if (session !== sessionIdRef.current) return;
    if (!stopped.ok) {
      setStatusBoth('idle');
      reportError('unavailable');
      return;
    }
    if (heldMs < MIN_RECORDING_MS) {
      setStatusBoth('idle');
      return;
    }

    const result = await transcribeVoiceAudio({ uri: stopped.uri, role, language, sessionId });
    // Cancelled, unmounted or restarted while the request was in flight.
    if (session !== sessionIdRef.current) return;
    setStatusBoth('idle');

    if (!result.ok) {
      if (result.kind === 'cap_exceeded') {
        reachCap(result.usage);
        void usageQuery.refetch();
        return;
      }
      haptics.trigger('error');
      trackVoiceTranscribeFailed({ role, surface, errorKind: result.kind });
      reportError(result.kind === 'network' ? 'network' : 'unknown');
      return;
    }

    trackVoiceTranscribeSuccess({
      role,
      surface,
      durationMs: result.durationMs ?? stopped.durationMs,
      confidence: result.confidence,
    });
    void usageQuery.refetch();
    const text = result.transcript.replace(/\s+/g, ' ').trim();
    if (text) onTranscriptRef.current(text);
  }, [haptics, language, reachCap, reportError, role, sessionId, setStatusBoth, surface, usageQuery]);

  const cancel = useCallback(async () => {
    if (statusRef.current === 'idle') return;
    sessionIdRef.current += 1;
    setStatusBoth('idle');
    await recorderRef.current.cancel();
  }, [setStatusBoth]);

  const toggle = useCallback(() => {
    if (statusRef.current === 'listening') void stop();
    else if (statusRef.current === 'idle') void start();
    // While transcribing the button is a no-op: the text is on its way.
  }, [start, stop]);

  useEffect(() => {
    if (!enabled) void cancel();
  }, [cancel, enabled]);

  // Leaving the screen mid-session must not leave the mic open.
  useEffect(() => {
    return () => {
      sessionIdRef.current += 1;
      void recorderRef.current.cancel();
    };
  }, []);

  return {
    status,
    level: status === 'listening' ? recorder.level : 0,
    lastError,
    toggle,
    start,
    stop,
    cancel,
    isOverCap: usageQuery.data?.isOverCap === true,
    isOffline,
    permissionStatus,
    usage: usageQuery.data ?? undefined,
  };
}
